class HeimanBundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.dialog = this.querySelector('[data-bundle-dialog]');
    this.addButton = this.querySelector('[data-bundle-add]');
    this.error = this.querySelector('[data-bundle-error]');

    this.querySelector('[data-bundle-open]')?.addEventListener('click', () => this.openDialog());

    this.querySelectorAll('[data-bundle-close]').forEach((button) => {
      button.addEventListener('click', () => this.closeDialog());
    });

    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.closeDialog();
    });

    this.querySelectorAll('[data-bundle-toggle]').forEach((input) => {
      input.addEventListener('change', () => this.updateSummary());
    });

    this.querySelectorAll('[data-bundle-variant]').forEach((select) => {
      select.addEventListener('change', () => {
        const card = select.closest('[data-bundle-item]');
        if (card) this.updateCard(card);
        this.updateSummary();
      });
    });

    this.querySelectorAll('[data-bundle-quantity]').forEach((input) => {
      input.addEventListener('input', () => this.updateSummary());

      input.addEventListener('change', () => {
        this.normalizeQuantity(input);
        this.updateSummary();
      });
    });

    this.addButton?.addEventListener('click', () => this.addBundleToCart());

    this.querySelectorAll('[data-bundle-item]').forEach((card) => this.updateCard(card));

    this.updateSummary();
  }

  openDialog() {
    this.errorMessage('');

    if (this.dialog?.showModal) {
      this.dialog.showModal();
    } else if (this.dialog) {
      this.dialog.setAttribute('open', '');
    }
  }

  closeDialog() {
    if (this.dialog?.open) {
      this.dialog.close();
    } else {
      this.dialog?.removeAttribute('open');
    }
  }

  getCurrencyFormatter() {
    const currency = this.dataset.currency || 'USD';
    const locale = this.dataset.locale || 'en';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
      });
    } catch (_error) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    }
  }

  formatMoney(cents) {
    return this.getCurrencyFormatter().format((Number(cents) || 0) / 100);
  }

  getSelectedOption(card) {
    const select = card.querySelector('[data-bundle-variant]');
    return select?.selectedOptions?.[0] || null;
  }

  normalizeQuantity(input) {
    const min = Math.max(
      1,
      Number.parseInt(input.min || '1', 10) || 1
    );

    const step = Math.max(
      1,
      Number.parseInt(input.step || '1', 10) || 1
    );

    const max = input.max
      ? Number.parseInt(input.max, 10)
      : Infinity;

    let quantity = Number.parseInt(
      input.value || String(min),
      10
    );

    if (!Number.isFinite(quantity)) {
      quantity = min;
    }

    quantity = Math.max(min, quantity);

    quantity = min + Math.round((quantity - min) / step) * step;

    quantity = Math.min(max, quantity);

    input.value = String(quantity);

    return quantity;
  }

  updateCard(card) {
    const option = this.getSelectedOption(card);

    if (!option) return;

    const price = Number(option.dataset.price || 0);
    const compareAt = Number(option.dataset.compareAt || 0);

    const priceElement = card.querySelector('[data-bundle-price]');
    const compareElement = card.querySelector('[data-bundle-compare]');
    const imageElement = card.querySelector('.heiman-bundle-builder__image img');
    const quantityInput = card.querySelector('[data-bundle-quantity]');
    const toggle = card.querySelector('[data-bundle-toggle]');

    if (priceElement) {
      priceElement.textContent = this.formatMoney(price);
    }

    if (compareElement) {
      if (compareAt > price) {
        compareElement.textContent = this.formatMoney(compareAt);
        compareElement.hidden = false;
      } else {
        compareElement.textContent = '';
        compareElement.hidden = true;
      }
    }

    if (imageElement && option.dataset.image) {
      imageElement.src = option.dataset.image;
      imageElement.removeAttribute('srcset');
    }

    if (quantityInput) {
      quantityInput.min = option.dataset.min || '1';
      quantityInput.step = option.dataset.step || '1';

      if (option.dataset.max) {
        quantityInput.max = option.dataset.max;
      } else {
        quantityInput.removeAttribute('max');
      }

      this.normalizeQuantity(quantityInput);
    }

    const available = option.dataset.available === 'true';

    if (toggle) {
      toggle.disabled = !available;

      if (!available) {
        toggle.checked = false;
      }
    }

    if (quantityInput) {
      quantityInput.disabled = !available;
    }

    card.classList.toggle('is-unavailable', !available);
  }

  getSelectedItems() {
    const items = [];

    this.querySelectorAll('[data-bundle-item]').forEach((card) => {
      const toggle = card.querySelector('[data-bundle-toggle]');

      if (!toggle?.checked || toggle.disabled) return;

      const option = this.getSelectedOption(card);
      const quantityInput = card.querySelector('[data-bundle-quantity]');

      if (
        !option ||
        option.dataset.available !== 'true' ||
        !quantityInput
      ) {
        return;
      }

      const quantity = this.normalizeQuantity(quantityInput);
      const variantId = Number(option.value);

      if (variantId > 0 && quantity > 0) {
        items.push({
          id: variantId,
          quantity,
        });
      }
    });

    return items;
  }

  updateSummary() {
    let selectedProductCount = 0;
    let total = 0;
    let compareTotal = 0;

    this.querySelectorAll('[data-bundle-item]').forEach((card) => {
      const toggle = card.querySelector('[data-bundle-toggle]');
      const option = this.getSelectedOption(card);
      const quantityInput = card.querySelector('[data-bundle-quantity]');

      const isSelected = Boolean(
        toggle?.checked &&
        !toggle.disabled &&
        option?.dataset.available === 'true'
      );

      card.classList.toggle('is-selected', isSelected);

      if (!isSelected || !option || !quantityInput) return;

      const quantity = this.normalizeQuantity(quantityInput);
      const price = Number(option.dataset.price || 0);
      const compareAt = Number(option.dataset.compareAt || 0);

      selectedProductCount += 1;

      total += price * quantity;

      compareTotal += Math.max(price, compareAt) * quantity;
    });

    const countElement = this.querySelector('[data-bundle-count]');
    const totalElement = this.querySelector('[data-bundle-total]');
    const savingsRow = this.querySelector('[data-bundle-savings-row]');
    const savingsElement = this.querySelector('[data-bundle-savings]');

    const savings = Math.max(0, compareTotal - total);

    if (countElement) {
      countElement.textContent = String(selectedProductCount);
    }

    if (totalElement) {
      totalElement.textContent = this.formatMoney(total);
    }

    if (savingsElement) {
      savingsElement.textContent = this.formatMoney(savings);
    }

    if (savingsRow) {
      savingsRow.hidden = savings <= 0;
    }

    if (this.addButton) {
      this.addButton.disabled =
        selectedProductCount === 0 ||
        this.addButton.dataset.loading === 'true';
    }
  }

  errorMessage(message) {
    if (!this.error) return;

    this.error.textContent = message || '';
    this.error.hidden = !message;
  }

  async addBundleToCart() {
    const items = this.getSelectedItems();

    if (!items.length) {
      this.errorMessage('Select at least one available product.');
      return;
    }

    this.errorMessage('');

    this.addButton.disabled = true;
    this.addButton.dataset.loading = 'true';

    const originalText = this.addButton.textContent;

    this.addButton.textContent = 'Adding…';

    try {
      const sectionIds = [
        ...new Set(
          Array.from(
            document.querySelectorAll('cart-items-component[data-section-id]')
          )
            .map((component) => component.dataset.sectionId)
            .filter(Boolean)
        ),
      ];

      const payload = {
        items,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      };

      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(
          result.description ||
          result.message ||
          'Unable to add this bundle to your cart.'
        );
      }

      const cartResponse = await fetch(this.dataset.cartJsonUrl, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      const cart = cartResponse.ok
        ? await cartResponse.json()
        : undefined;

      this.closeDialog();

      // This theme uses the cart:update event to refresh cart sections and cart count.
      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.id,
            data: {
              source: 'heiman-bundle-builder',
              itemCount: items.reduce(
                (sum, item) => sum + item.quantity,
                0
              ),
              sections: result.sections || {},
            },
          },
        })
      );

      const drawer = document.querySelector('cart-drawer-component');

      if (drawer) {
        if (
          !drawer.hasAttribute('auto-open') &&
          typeof drawer.open === 'function'
        ) {
          drawer.open();
        }
      } else {
        window.location.assign(
          this.dataset.cartUrl || '/cart'
        );
      }

    } catch (error) {
      this.errorMessage(
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.'
      );

    } finally {
      this.addButton.dataset.loading = 'false';
      this.addButton.textContent = originalText;

      this.updateSummary();
    }
  }
}

if (!customElements.get('heiman-bundle-builder')) {
  customElements.define(
    'heiman-bundle-builder',
    HeimanBundleBuilder
  );
}